const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary);
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  return bytesToBase64(bytes)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

export function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

export function randomBase64(length = 32): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytesToBase64(bytes);
}

export async function sha256(
  value: string | ArrayBuffer | Uint8Array,
): Promise<string> {
  const data =
    typeof value === 'string'
      ? textEncoder.encode(value)
      : value instanceof Uint8Array
        ? value
        : new Uint8Array(value);
  return bytesToBase64Url(
    new Uint8Array(await crypto.subtle.digest('SHA-256', data as BufferSource)),
  );
}

interface StoredKeys {
  publicKey: JsonWebKey;
  privateKey: JsonWebKey;
}

export class E2ECrypto {
  private constructor(
    private readonly pair: CryptoKeyPair,
    public readonly publicJwk: JsonWebKey,
  ) {}

  static async load(deviceId: string): Promise<E2ECrypto> {
    const key = `drop.ecdh.${deviceId}`;
    let stored: StoredKeys | undefined;
    try {
      stored = JSON.parse(
        localStorage.getItem(key) ?? 'null',
      ) as StoredKeys | undefined;
    } catch {
      /* regenerate below */
    }

    if (stored?.publicKey && stored.privateKey) {
      try {
        const [publicKey, privateKey] = await Promise.all([
          crypto.subtle.importKey(
            'jwk',
            stored.publicKey,
            { name: 'ECDH', namedCurve: 'P-256' },
            true,
            [],
          ),
          crypto.subtle.importKey(
            'jwk',
            stored.privateKey,
            { name: 'ECDH', namedCurve: 'P-256' },
            true,
            ['deriveKey'],
          ),
        ]);
        return new E2ECrypto({ publicKey, privateKey }, stored.publicKey);
      } catch {
        /* regenerate below */
      }
    }

    const pair = await crypto.subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' },
      true,
      ['deriveKey'],
    );
    const [publicKey, privateKey] = await Promise.all([
      crypto.subtle.exportKey('jwk', pair.publicKey),
      crypto.subtle.exportKey('jwk', pair.privateKey),
    ]);
    try {
      localStorage.setItem(
        key,
        JSON.stringify({ publicKey, privateKey } satisfies StoredKeys),
      );
    } catch {
      /* private browsing */
    }
    return new E2ECrypto(pair, publicKey);
  }

  async encrypt(
    peerPublic: JsonWebKey | undefined,
    plaintext: Uint8Array,
  ): Promise<Uint8Array> {
    if (!peerPublic) throw new Error('Peer encryption key is unavailable');
    const key = await this.shared(peerPublic);
    const iv = new Uint8Array(12);
    crypto.getRandomValues(iv);
    const ciphertext = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        plaintext as BufferSource,
      ),
    );
    const output = new Uint8Array(iv.length + ciphertext.length);
    output.set(iv);
    output.set(ciphertext, iv.length);
    return output;
  }

  async decrypt(
    peerPublic: JsonWebKey | undefined,
    input: Uint8Array,
  ): Promise<Uint8Array> {
    if (!peerPublic || input.length < 13) {
      throw new Error('Peer encryption key is unavailable');
    }
    const key = await this.shared(peerPublic);
    return new Uint8Array(
      await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: input.subarray(0, 12) as BufferSource },
        key,
        input.subarray(12) as BufferSource,
      ),
    );
  }

  private async shared(peerPublic: JsonWebKey): Promise<CryptoKey> {
    const publicKey = await crypto.subtle.importKey(
      'jwk',
      peerPublic,
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      [],
    );
    return crypto.subtle.deriveKey(
      { name: 'ECDH', public: publicKey },
      this.pair.privateKey!,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
  }
}

export const utf8 = (value: string): Uint8Array => textEncoder.encode(value);
export const text = (value: Uint8Array): string => textDecoder.decode(value);
